import { v4 as uuidv4 } from 'uuid';
import { validate as isValidUUID } from 'uuid';
import ChatBox from '../chat/ChatBox';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  combineMessagesWithClient,
  copyToAssistantInput,
  exportMessagesPlainText,
  exportMessagesCsv,
  pushUserToAssistantMessage,
  readAllMessagesWithClient,
  selectMessagesWithClient,
} from '../../store/chat/chatSlice';
import ChatView from '../chat/ChatView';
import {
  selectCurrentActor,
  selectCurrentClient,
} from '../../store/client/clientSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Input,
  Tooltip,
} from '@nextui-org/react';
import {
  deleteChatWithClient,
  directChat,
  evaluateClientMessageSentiment,
  evaluateClientMessageSuicidality,
  queryActor,
  querySelfPlay,
  saveChatsToDatabase,
  TOAST_NO_MESSAGES,
} from '../../store/chat/chatThunks';
import { evaluateRiskOfChat } from '../../store/assistant/assistantThunks';
import { FaDatabase, FaTrafficLight, FaTrash } from 'react-icons/fa6';
import Icon from '../base/Icon';
import ButtonWithConfirmModal from '../base/ButtonWithConfirmModal';
import { QueryActorReqDto } from '../../store/chat/chatDto';
import {
  selectActorConfig,
  selectSettingsAnalytics,
  selectSettingsDeveloper,
} from '../../store/settings/settingsSlice';
import ChatInputClient from '../chat/input/ChatInputClient';
import AnimatePresence from '../base/AnimatePresence';
import ButtonExportCsv from '../base/ButtonExportCsv';
import ButtonExportTxt from '../base/ButtonExportTxt';
import {
  selectDisplayName,
  selectUserRole,
  selectUserName,
} from '../../store/auth/authSlice';
import { TraineeBubble } from '../../types';
import {
  selectRiskOfChats,
  selectRiskOfMessages,
} from '../../store/assistant/assistantSlice';
import { isActorType } from '../chat/ChatListClients';
import { toast } from 'react-toastify';

const MIN_RALLY_COUNT = 0;

export default function ClientDialogue() {
  const dispatch = useAppDispatch();
  const currentClient = useAppSelector(selectCurrentClient);
  const currentActor = useAppSelector(selectCurrentActor);
  const messagesWithClient = useAppSelector(selectMessagesWithClient);
  const settingsAnalytics = useAppSelector(selectSettingsAnalytics);
  const settingsDev = useAppSelector(selectSettingsDeveloper);
  const userRole = useAppSelector(selectUserRole);
  const displayName = useAppSelector(selectDisplayName);
  const userName = useAppSelector(selectUserName);
  const messageRisk = useAppSelector(selectRiskOfMessages);
  const riskOfChats = useAppSelector(selectRiskOfChats);

  // common actor control states
  const actorConfig = useAppSelector(selectActorConfig);
  const selectedTypingSimulated = useMemo(
    () => actorConfig?.typingSimulated,
    [actorConfig]
  );
  const selectedAutomaticReplies = useMemo(
    () => actorConfig?.automaticReplies,
    [actorConfig]
  );

  const [actorMode, setActorMode] = useState<'standard' | 'selfplay'>(
    'standard'
  );
  const [actorLocalTimeLabel, setActorLocalTimeLabel] = useState<
    string | undefined
  >(undefined);
  const [actorTimeTag, setActorTimeTag] = useState<string | undefined>(
    undefined
  );
  // currentClient.userType is always 'client' for DB-restored chats, so also
  // check currentActor (populated from actor profiles, unaffected by DB mapping)
  const isNonActorClient = useMemo(
    () => currentClient?.userType !== 'actor' && !currentActor,
    [currentClient, currentActor]
  );

  // actor's standard control states
  /**
   * Whether the last message came from a user;
   * used to trigger actor replies
   */
  const isLastMessageUser = useMemo(() => {
    if (!messagesWithClient) {
      return false;
    }
    const n = messagesWithClient.length;
    return n > 0 && messagesWithClient[n - 1].role === 'user';
  }, [messagesWithClient]);

  // actor's self-play control states
  /**
   * Number of back-and-forths to perform during self-play.
   */
  const [rallyCount, setRallyCount] = useState(MIN_RALLY_COUNT);
  /**
   * Whether actor is already replying, so that
   * duplicate actor replies are not triggered.
   */
  const [isRallyWait, setIsRallyWait] = useState(false);
  /**
   * Whether actor is serving the first message.
   */
  const [isActorServing, setIsActorServing] = useState(false);
  const clientName = useMemo(() => {
    if (currentClient?.userType === 'actor') {
      return currentActor?.name || currentClient.name || currentClient.username;
    }

    return (
      currentClient?.friendlyName ||
      currentClient?.username ||
      currentClient?.userId
    );
  }, [currentClient, currentActor]);

  useEffect(() => {
    const startTime = currentActor?.local_time_start;

    const compute = () => {
      if (!startTime) {
        setActorLocalTimeLabel(undefined);
        setActorTimeTag(undefined);
        return;
      }

      const match = /^(\d{1,2}):(\d{2})$/.exec(startTime.trim());
      if (!match) {
        setActorLocalTimeLabel(`Local time: ${startTime}`);
        setActorTimeTag(undefined);
        return;
      }

      const startHour = Number(match[1]);
      const startMinute = Number(match[2]);
      if (
        Number.isNaN(startHour) ||
        Number.isNaN(startMinute) ||
        startHour < 0 ||
        startHour > 23 ||
        startMinute < 0 ||
        startMinute > 59
      ) {
        setActorLocalTimeLabel(`Local time: ${startTime}`);
        setActorTimeTag(undefined);
        return;
      }

      const firstMessageTime = messagesWithClient[0]?.createdAt;
      const sessionStartTs = firstMessageTime
        ? Date.parse(firstMessageTime)
        : NaN;
      const elapsedSeconds = Number.isNaN(sessionStartTs)
        ? 0
        : Math.max(0, Math.floor((Date.now() - sessionStartTs) / 1000));
      const startSeconds = startHour * 3600 + startMinute * 60;
      const daySeconds = 24 * 3600;
      const currentSeconds = (startSeconds + elapsedSeconds) % daySeconds;
      const hour = Math.floor(currentSeconds / 3600);
      const minute = Math.floor((currentSeconds % 3600) / 60);
      const display = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      setActorLocalTimeLabel(`Local time: ${display}`);

      if (hour >= 5 && hour < 8) setActorTimeTag('early morning');
      else if (hour >= 8 && hour < 12) setActorTimeTag('morning');
      else if (hour >= 12 && hour < 17) setActorTimeTag('afternoon');
      else if (hour >= 17 && hour < 21) setActorTimeTag('evening');
      else if (hour >= 21 && hour < 24) setActorTimeTag('night');
      else setActorTimeTag('late night');
    };

    compute();
    if (!startTime) return;
    const timerId = window.setInterval(compute, 1000);
    return () => window.clearInterval(timerId);
  }, [currentActor?.local_time_start, messagesWithClient]);

  const queryActorDto: QueryActorReqDto = useMemo(() => {
    const n = messagesWithClient.length;
    const actor = currentActor?.userId || '';
    const prompt = currentActor?.prompt;
    const firstname = currentActor?.name;
    const isLastMessageUser =
      n > 0 && messagesWithClient[n - 1].role === 'user';
    const content = isLastMessageUser ? messagesWithClient[n - 1].content : '';
    const history = combineMessagesWithClient(
      isLastMessageUser
        ? messagesWithClient.slice(0, n - 1)
        : messagesWithClient
    );
    return { actor, prompt, content, history, firstname };
  }, [currentActor, messagesWithClient]);

  const handleActorSelfPlayStart = () => {
    if (rallyCount < 1) {
      return;
    }
    const lastMessage = messagesWithClient[messagesWithClient.length - 1];
    const lastIsFromActor =
      messagesWithClient.length > 0 && lastMessage?.role === 'assistant';
    if (lastIsFromActor) {
      // Actor already spoke — generate a client response first, then let the
      // regular self-play loop take over once isLastMessageUser becomes true.
      dispatch(querySelfPlay());
      setActorMode('selfplay');
      setIsActorServing(false);
    } else {
      // No history, or last message is from the user — actor goes first.
      setActorMode('selfplay');
      setIsActorServing(true);
    }
  };

  const handleActorStandardStart = () => {
    triggerStandard();
  };

  let isCustomActor = false;
  if (currentActor?.userId) {
    isCustomActor = isValidUUID(currentActor?.userId);
  }

  const triggerStandard = useCallback(() => {
    if (!currentClient?.userId) {
      return;
    }
    dispatch(
      queryActor(
        queryActorDto,
        currentClient.userId,
        selectedTypingSimulated,
        false,
        isCustomActor,
        userRole === 'user_trainee'
      )
    );
  }, [
    dispatch,
    currentClient?.userId,
    queryActorDto,
    selectedTypingSimulated,
    isCustomActor,
    userRole,
  ]);

  const triggerSelfPlay = useCallback(() => {
    if (!currentClient?.userId) {
      return;
    }
    setIsRallyWait(true);
    setIsActorServing(false);
    setRallyCount((c) => c - 1);
    dispatch(
      queryActor(
        queryActorDto,
        currentClient.userId,
        selectedTypingSimulated,
        true,
        isCustomActor,
        userRole === 'user_trainee'
      )
    );
  }, [
    dispatch,
    currentClient?.userId,
    queryActorDto,
    selectedTypingSimulated,
    isCustomActor,
    userRole,
  ]);

  useEffect(() => {
    if (!messagesWithClient || !currentClient) {
      return;
    }
    let unreadCount = 0;
    for (const message of messagesWithClient) {
      if (message.isUnread) {
        unreadCount += 1;
      }

      if (
        message.role === 'assistant' &&
        message.content &&
        typeof message.contentSentiment !== 'number' &&
        !message.sentimentProcessing
      ) {
        // evaluate sentiment of unread message
        if (settingsAnalytics.autoEvaluateSentiment) {
          dispatch(evaluateClientMessageSentiment(message.messageId));
        }
      }

      if (
        message.role === 'assistant' &&
        message.content &&
        typeof message.contentSuicideScore !== 'number' &&
        !message.suicidalityProcessing
      ) {
        // evaluate suicidality of unread message
        if (settingsAnalytics.evaluateSuicidality) {
          dispatch(evaluateClientMessageSuicidality(message.messageId));
        }
      }
    }

    if (unreadCount === 0) {
      if (settingsAnalytics.autoTrafficLight && messagesWithClient.length) {
        const assistantCount = messagesWithClient.reduce(
          (count, m) => count + (m.role === 'assistant' ? 1 : 0),
          0
        );
        if (Object.keys(riskOfChats).length === 0) {
          if (
            messagesWithClient[messagesWithClient.length - 1].content !== '' &&
            messagesWithClient[messagesWithClient.length - 1].role ===
              'assistant'
          ) {
            dispatch(
              evaluateRiskOfChat(
                currentClient.userId,
                messagesWithClient,
                settingsAnalytics.reasoning
              )
            );
          }
        } else {
          if (
            riskOfChats[currentClient.userId]?.clientMessages !== assistantCount
          ) {
            if (
              messagesWithClient[messagesWithClient.length - 1].content !==
                '' &&
              messagesWithClient[messagesWithClient.length - 1].role ===
                'assistant'
            ) {
              dispatch(
                evaluateRiskOfChat(
                  currentClient.userId,
                  messagesWithClient,
                  settingsAnalytics.reasoning
                )
              );
            }
          }
        }
      }
      return;
    }
    // read all messages on chat opened
    dispatch(readAllMessagesWithClient(currentClient.userId));

    if (
      messagesWithClient[messagesWithClient.length - 1]?.role === 'assistant'
    ) {
      // actor has replied; ready for next rally
      setIsRallyWait(false);
    }
  }, [
    dispatch,
    messagesWithClient,
    currentClient,
    settingsAnalytics,
    messageRisk,
    riskOfChats,
  ]);

  // actor control effects for standard conversation
  useEffect(() => {
    if (actorMode !== 'standard' || isNonActorClient || isRallyWait) {
      return;
    }
    if (
      (isLastMessageUser && selectedAutomaticReplies) ||
      (isLastMessageUser && userRole === 'user_trainee')
    ) {
      triggerStandard();
    }
  }, [
    actorMode,
    isNonActorClient,
    isRallyWait,
    isLastMessageUser,
    selectedAutomaticReplies,
    triggerStandard,
    userRole,
  ]);

  // actor control effects for self-play
  useEffect(() => {
    if (actorMode !== 'selfplay' || isNonActorClient || isRallyWait) {
      return;
    }
    if (rallyCount < 1) {
      setActorMode('standard');
      return;
    }
    if (isLastMessageUser || isActorServing) {
      triggerSelfPlay();
    }
  }, [
    actorMode,
    isNonActorClient,
    isRallyWait,
    rallyCount,
    isLastMessageUser,
    isActorServing,
    triggerSelfPlay,
  ]);

  const handleTransfer = (message: string, sendImmediately = false) => {
    const userId = currentClient?.userId;
    if (!userId) {
      return;
    }
    const payload = message.trim();
    if (sendImmediately) {
      dispatch(
        pushUserToAssistantMessage({
          userId,
          payload,
          messageId: uuidv4(),
        })
      );
      dispatch(directChat(payload));
    } else {
      dispatch(copyToAssistantInput({ userId, payload }));
    }
  };

  const handleClear = () => {
    if (currentClient?.userId) {
      dispatch(deleteChatWithClient(currentClient.userId));
    }
  };

  const handleExportPlainText = () => {
    if (messagesWithClient.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    if (currentClient && messagesWithClient) {
      exportMessagesPlainText(
        messagesWithClient,
        {
          messagesLabel: `${clientName} [${currentClient.userId}] dialog`,
          assistantLabel: `CLIENT ${clientName}`,
          userLabel: 'RESPONDER',
          dialogue: 'client',
        },
        {},
        messageRisk
      );
    }
  };

  const handleExportCsv = () => {
    if (messagesWithClient.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    if (currentClient && messagesWithClient) {
      exportMessagesCsv(
        messagesWithClient,
        {
          messagesLabel: `${clientName} [${currentClient.userId}] dialog`,
          assistantLabel: `CLIENT ${clientName}`,
          userLabel: 'RESPONDER',
          dialogue: 'client',
        },
        {},
        messageRisk
      );
    }
  };

  const handleSaveChat = useCallback(async () => {
    if (!currentClient || messagesWithClient.length === 0) return;

    const chatId = uuidv4();
    const bubbles: TraineeBubble[] = messagesWithClient.map((message) => ({
      chatId,
      messageId: message.messageId,
      role: message.role,
      name:
        (message.role === 'assistant' ? currentActor?.name : displayName) || '',
      content: message.content,
      original_created_at: message.createdAt,
      isUnread: message.isUnread || false,
    }));

    const bubbleInfo = {
      chatId,
      owner: userName || displayName || '',
      chatName: `${displayName} - ${currentActor?.name} (Actor) Conversation`,
      feedback: '',
      reviewer: '',
      status: 'ongoing',
      stage: '',
      notes: '',
    };

    const success = await dispatch(saveChatsToDatabase(bubbles, bubbleInfo));
    if (success) {
      dispatch(deleteChatWithClient(currentClient.userId));
    }
  }, [
    currentClient,
    messagesWithClient,
    currentActor,
    displayName,
    userName,
    dispatch,
  ]);

  const handleEvaluateChatRisk = () => {
    if (messagesWithClient && currentClient) {
      dispatch(
        evaluateRiskOfChat(
          currentClient.userId,
          messagesWithClient,
          settingsAnalytics.reasoning
        )
      );
    }
  };

  const handleRallyCountChange = (value: string) => {
    setRallyCount(parseInt(value) || MIN_RALLY_COUNT);
  };

  const buttonTrafficLight = (
    <Tooltip content="Evaluate suicidal risk" placement="bottom">
      <Button onPress={handleEvaluateChatRisk}>
        <Icon icon={FaTrafficLight} />
      </Button>
    </Tooltip>
  );

  const buttonExportTxt = <ButtonExportTxt onPress={handleExportPlainText} />;

  const buttonExportCsv = <ButtonExportCsv onPress={handleExportCsv} />;

  const buttonSaveChat = (
    <ButtonWithConfirmModal
      onPress={handleSaveChat}
      buttonContent={<Icon icon={FaDatabase} className="w-3.5 h-3.5" />}
      buttonProps={{ isDisabled: !messagesWithClient?.length }}
      tooltipProps={{ content: 'Submit for Review', placement: 'bottom' }}
      modalHeader="Are you sure you want to submit this conversation for review?"
      modalBody="This will clear the conversation from your chat window. You will not be able send messages to this conversation."
      confirmLabel="Submit"
    />
  );

  const buttonClear = (
    <ButtonWithConfirmModal
      onPress={handleClear}
      buttonContent={<Icon icon={FaTrash} />}
      buttonProps={{ color: 'danger', isDisabled: !messagesWithClient?.length }}
      tooltipProps={{
        content: 'Clear conversation',
        placement: 'bottom',
        color: 'danger',
      }}
      confirmLabel="Clear"
      modalHeader="Clear conversation?"
    />
  );

  const isActor = isActorType(currentActor?.userId);

  const actorControls =
    (currentClient?.userType === 'actor' || isActor) &&
    (settingsDev.showSelfPlayOptions || messagesWithClient.length === 0) ? (
      <Card shadow="none" className="border-1">
        <CardBody className="flex flex-col gap-2">
          {settingsDev.showSelfPlayOptions && (
            <Input
              size="sm"
              type="number"
              label="Number of back-and-forths"
              placeholder="e.g. 10"
              value={rallyCount.toString()}
              onValueChange={handleRallyCountChange}
              min={MIN_RALLY_COUNT}
            />
          )}
        </CardBody>
        <CardFooter className="card-footer">
          <ButtonGroup variant="flat">
            {messagesWithClient.length === 0 && (
              <Button onPress={handleActorStandardStart}>Start</Button>
            )}
            {settingsDev.showSelfPlayOptions && (
              <Button
                color="success"
                onPress={handleActorSelfPlayStart}
                isDisabled={rallyCount < 1}
                isLoading={actorMode === 'selfplay'}
              >
                Self-play
              </Button>
            )}
          </ButtonGroup>
        </CardFooter>
      </Card>
    ) : undefined;

  const chatBox = (
    <AnimatePresence mode="wait">
      {currentClient && (
        <ChatBox
          key={currentClient.userId}
          messages={messagesWithClient}
          onTransfer={handleTransfer}
          bottomIndicator={
            !settingsDev.showSelfPlayOptions ? actorControls : undefined
          }
          headerTitle={clientName}
          headerTag={actorTimeTag}
          headerSubtitle={actorLocalTimeLabel}
          headerElements={[
            <ButtonGroup
              key="chat-actions"
              isIconOnly
              variant="light"
              className="client-button-group"
            >
              {userRole !== 'user_trainee' &&
                settingsAnalytics.showTrafficLight &&
                buttonTrafficLight}
              {buttonExportTxt}
              {buttonExportCsv}
              {(userRole === 'user_trainee' ||
                userRole === 'admin' ||
                userRole === 'user_sos') &&
                !isNonActorClient &&
                buttonSaveChat}
              {buttonClear}
            </ButtonGroup>,
          ]}
          inputField={
            <ChatInputClient
              selfPlayCard={
                settingsDev.showSelfPlayOptions ? actorControls : undefined
              }
            />
          }
        />
      )}
    </AnimatePresence>
  );

  return (
    <ChatView
      chatId={currentClient?.userId}
      chatBox={chatBox}
      chatType="client"
    />
  );
}
